export const buildSignaturesTemplate = () => ({
  "client.buy": {
    integrationRole: "Buyer",
    meviDealRole: "client.buy",
    min: 1,
    max: 6,
    default: 2,
    additionalSignaturePage: 3,
    altSigners: [],
    signatures: Array.from({ length: 6 }, (_, i) => ({
      signerKey: `Buyer${i + 1}`,
      email: `Buyer${i + 1}@mevi.io`,
    })),
  },
  "client.sell": {
    integrationRole: "Seller",
    meviDealRole: "client.sell",
    min: 1,
    max: 6,
    default: 2,
    additionalSignaturePage: 3,
    altSigners: [],
    signatures: Array.from({ length: 6 }, (_, i) => ({
      signerKey: `Seller${i + 1}`,
      email: `Seller${i + 1}@mevi.io`,
    })),
  },
  "team.buy": {
    integrationRole: "BuyerAgent",
    meviDealRole: "team.buy",
    min: 1,
    max: 5,
    default: 1,
    additionalSignaturePage: 3,
    altSigners: [],
    signatures: Array.from({ length: 5 }, (_, i) => ({
      signerKey: `BuyerAgent${i + 1}`,
      email: `BuyerAgent${i + 1}@mevi.io`,
    })),
  },
  "team.sell": {
    integrationRole: "SellerAgent",
    meviDealRole: "team.sell",
    min: 1,
    max: 5,
    default: 1,
    additionalSignaturePage: 3,
    altSigners: [],
    signatures: Array.from({ length: 5 }, (_, i) => ({
      signerKey: `SellerAgent${i + 1}`,
      email: `SellerAgent${i + 1}@mevi.io`,
    })),
  },
  "other.managingBroker": {
    integrationRole: "ManagingBroker",
    meviDealRole: "other.managingBroker",
    min: 1,
    max: 1,
    default: 1,
    additionalSignaturePage: 3,
    altSigners: [],
    signatures: Array.from({ length: 1 }, (_, i) => ({
      signerKey: `ManagingBroker${i + 1}`,
      email: `ManagingBroker${i + 1}@mevi.io`,
    })),
  },
  "other.propertyManager": {
    integrationRole: "PropertyManager",
    meviDealRole: "other.propertyManager",
    min: 1,
    max: 1,
    default: 1,
    additionalSignaturePage: 3,
    altSigners: [],
    signatures: Array.from({ length: 1 }, (_, i) => ({
      signerKey: `PropertyManager${i + 1}`,
      email: `PropertyManager${i + 1}@mevi.io`,
    })),
  },
});

export const buildSignaturesJSON = (selectedRoles = []) => {
  if (!selectedRoles || selectedRoles.length === 0) {
    return {};
  }

  const template = buildSignaturesTemplate();
  return Object.fromEntries(
    selectedRoles
      .map((role) => [role, template[role]])
      .filter(([, value]) => !!value)
  );
};

export const getRoleSignatureKeys = (roleKey) => {
  const template = buildSignaturesTemplate();
  const role = template[roleKey];
  if (!role?.signatures) return [];
  return role.signatures.map((signature) => signature.signerKey).filter(Boolean);
};

/**
 * Default signer key + email for a new row in a role, matching {@link buildSignaturesTemplate}
 * (e.g. Buyer2 / Buyer2@mevi.io for client.buy; SellerAgent2 for team.sell).
 * Picks the smallest N such that `${prefix}${N}` is not already used.
 *
 * @param {string} roleKey - e.g. client.buy
 * @param {Iterable<string>} existingSignerKeys - signerKey values already present for this role
 * @returns {{ signerKey: string, email: string }}
 */
export function getNextSignerKeyAndEmail(roleKey, existingSignerKeys) {
  const template = buildSignaturesTemplate();
  const templateFirst = template[roleKey]?.signatures?.[0];
  const key0 = templateFirst?.signerKey || "";
  const m = key0.match(/^(.+?)(\d+)$/);
  const prefix = m ? m[1] : "Signer";
  const existing = new Set(
    [...existingSignerKeys].map((k) => String(k || "").trim()).filter(Boolean),
  );
  let n = 1;
  let signerKey = `${prefix}${n}`;
  while (existing.has(signerKey)) {
    n += 1;
    signerKey = `${prefix}${n}`;
  }
  return { signerKey, email: `${signerKey}@mevi.io` };
}

export const getAllSignatureRoleKeys = () =>
  Object.keys(buildSignaturesTemplate());

/**
 * Template-generated default role labels (for display). Phase 4 UI overrides are stored in signatures[roleKey].label.
 * @returns {Record<string, string>} roleKey -> display label
 */
export const getDefaultRoleLabels = () => {
  const template = buildSignaturesTemplate();
  return Object.fromEntries(
    Object.entries(template).map(([roleKey, role]) => {
      const integrationRole = role.integrationRole || roleKey;
      const label = integrationRole.replace(/([a-z])([A-Z])/g, "$1 $2");
      return [roleKey, label];
    })
  );
};

/** Unique id for studio PDF annotations (signature/initials placement). */
export function createStudioAnnotationId(prefix) {
  const p = prefix || "annotation";
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${p}-${crypto.randomUUID()}`;
  }
  return `${p}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Derive signatureAnnotations from signatures (e.g. after import or JSON edit).
 * Each signer with pdfLocation becomes one annotation with a stable id.
 * @param {Object} signatures - roleKey -> { signatures: [{ signerKey, email?, pdfLocation? }] }
 * @returns {Array<{ id, roleKey, signerKey, pdfLocation }>}
 */
export function deriveSignatureAnnotationsFromSignatures(signatures) {
  if (!signatures || typeof signatures !== "object") return [];
  const out = [];
  for (const [roleKey, roleValue] of Object.entries(signatures)) {
    const sigs = Array.isArray(roleValue?.signatures) ? roleValue.signatures : [];
    sigs.forEach((sig, idx) => {
      if (!sig?.signerKey) return;
      // One annotation per rect: a signer with several tabs gets several.
      tabRectsOf(sig).forEach((pdfLocation, tabIdx) => {
        out.push({
          id: `signature-${roleKey}-${sig.signerKey}-${idx}-${tabIdx}`,
          roleKey,
          signerKey: sig.signerKey,
          pdfLocation,
        });
      });
    });
  }
  return out;
}

/** Legacy minimal placements on a signer row (`additionalFields` / attributePlacements / additionalPlacements). */
function getSignerLegacyPlacementRows(sig) {
  if (!sig || typeof sig !== "object") return null;
  if (Array.isArray(sig.additionalFields)) return sig.additionalFields;
  if (Array.isArray(sig.attributePlacements)) return sig.attributePlacements;
  if (Array.isArray(sig.additionalPlacements)) return sig.additionalPlacements;
  return null;
}

function pushBindingsFromPdfSlices(bindings, roleKey, signerKey, attributeName, rawPdfLocation, linesFallback, ts, nRef, extra) {
  const locList = Array.isArray(rawPdfLocation) ? rawPdfLocation : [rawPdfLocation];
  locList.forEach((locItem) => {
    const loc = locItem && typeof locItem === "object" ? { ...locItem } : null;
    const lines = loc?.lines ?? linesFallback ?? 0;
    bindings.push({
      id: `signer-bind-${ts}-${nRef.n++}`,
      roleKey,
      signerKey,
      attributeName: attributeName ?? "",
      pdfLocation: loc,
      lines,
      ...(extra || {}),
    });
  });
}

/**
 * Field-definition keys that belong to a signer field's editor/publish payload but must NOT be
 * persisted onto its placement binding — isolated app state, placement, or synthetic-editor plumbing.
 */
const SIGNER_FIELD_DEF_OMIT_KEYS = new Set([
  "id",
  // `order` is NOT omitted: it is what sequences a signer row's fields[] on publish
  // (buildSignerRowFieldsFromPlacements sorts by it). Stripping it here flattened every field
  // to the stub's hardcoded 1, so field order was whatever order the bindings happened to sit
  // in — and an edited field would silently jump position in the published JSON.
  "rules",
  "offerCompare",
  "linkedFieldId",
  "pdfEditorSyntheticKind",
  "pdfExtraSignerSelectionCompound",
  "pdfExtraOutputTarget",
  "pdfPersonaSubtype",
  "preserveAttributeNameCasing",
  "_pdfExtraOutputTarget",
  "_pdfPersonaSubtype",
  "_pdfSyntheticSignerCompound",
  "_pdfSyntheticSubsequentSigners",
  "touchedByAnnotator",
  "touchedByJson",
  "signers",
]);

/**
 * Snapshot a signer field's full definition (type, validation, hint, info, placeholder, metadata, …)
 * so it round-trips through its placement binding exactly like a regular form field. Placement
 * (`metadata.pdfLocation`) is intentionally dropped — that lives on the binding itself.
 * @param {Object} field
 * @returns {Object|null}
 */
export function snapshotSignerFieldDef(field) {
  if (!field || typeof field !== "object") return null;
  const def = {};
  for (const [k, v] of Object.entries(field)) {
    if (SIGNER_FIELD_DEF_OMIT_KEYS.has(k)) continue;
    def[k] = v;
  }
  if (def.metadata && typeof def.metadata === "object" && !Array.isArray(def.metadata)) {
    const { pdfLocation, pdfLocationContinuations, ...restMeta } = def.metadata;
    def.metadata = Object.keys(restMeta).length > 0 ? restMeta : undefined;
  }
  // "text" is the synthetic placement-editor's placeholder, not a real Mevi field type;
  // never let it persist into a signer field's definition.
  if (!def.type || def.type === "text") {
    def.type = "input";
  }
  return def;
}

/**
 * Extract persona-related PDF bindings from signaturesJSON (`fields` as full form field rows, or legacy `additionalFields`).
 * @param {Object} signatures - roleKey -> { signatures: [...] }
 * @returns {Array<{ id, roleKey, signerKey, attributeName, pdfLocation, lines, def }>}
 */
/**
 * The signer rows on a role, from either key.
 *
 * `signaturesJSON` roles carry `signatures`; `initialsJSON` roles carry `initials`. Older bundles
 * published initials under `signatures` too, so both are accepted on read — a form loaded from an
 * old bundle republishes under the new key. Nothing writes `signatures` into initialsJSON any more.
 */
/** Signer-key inventory: base → max slot. Mirrors scripts/audit-signer-fields.js. */
const SIGNER_BASES = {
  Buyer: 6,
  Seller: 6,
  BuyerAgent: 6,
  SellerAgent: 6,
  Closing: 1,
  ClosingOrAdmin: 1,
  ManagingBroker: 6,
  PropertyManager: 1,
};

/** Every valid signer key, longest first so `BuyerAgent1` wins over `Buyer`. */
export const SIGNER_KEYS = Object.entries(SIGNER_BASES)
  .flatMap(([base, max]) => Array.from({ length: max }, (_, i) => `${base}${i + 1}`))
  .sort((a, b) => b.length - a.length || a.localeCompare(b));

/** The signer key an attributeName is prefixed with (`Buyer1_SigDate` → `Buyer1`), or "". */
export function signerKeyFromAttribute(attributeName) {
  const attr = String(attributeName || "");
  return SIGNER_KEYS.find((k) => attr.startsWith(k)) || "";
}

/**
 * The tab rects on one published signer/initials row, from either key.
 *
 * A row carries `pdfLocation` (single tab) or `pdfLocations[]` (several) — the two are XOR by
 * contract. Reading both means a multi-tab form survives an import; reading only the singular
 * would drop every extra tab on the next round-trip.
 */
export function tabRectsOf(row) {
  if (Array.isArray(row?.pdfLocations)) return row.pdfLocations.filter(Boolean);
  return row?.pdfLocation ? [row.pdfLocation] : [];
}

export function signerRowsOf(roleValue) {
  if (Array.isArray(roleValue?.initials)) return roleValue.initials;
  if (Array.isArray(roleValue?.signatures)) return roleValue.signatures;
  return [];
}

export function extractSignerAttributeBindingsFromSignatures(signatures) {
  const bindings = [];
  if (!signatures || typeof signatures !== "object") return bindings;
  const nRef = { n: 0 };
  const ts = Date.now();
  for (const [roleKey, roleValue] of Object.entries(signatures)) {
    const sigs = signerRowsOf(roleValue);
    sigs.forEach((sig) => {
      if (!sig?.signerKey) return;
      const legacy = getSignerLegacyPlacementRows(sig);
      if (Array.isArray(legacy) && legacy.length > 0) {
        legacy.forEach((ap) => {
          const rawPdfLocation = ap?.pdfLocation;
          if (rawPdfLocation == null) return;
          pushBindingsFromPdfSlices(
            bindings,
            roleKey,
            sig.signerKey,
            ap.attributeName ?? "",
            rawPdfLocation,
            ap.lines,
            ts,
            nRef,
          );
        });
      }
      const fieldRows = Array.isArray(sig.fields) ? sig.fields : [];
      fieldRows.forEach((pf) => {
        if (!pf || typeof pf !== "object") return;
        const an = pf.attributeName;
        if (!an) return;
        const def = snapshotSignerFieldDef(pf);
        const extra = {};
        if (pf.label != null && pf.label !== "") extra.label = pf.label;
        if (def) extra.def = def;
        const rawPdfLocation = pf.metadata?.pdfLocation;
        if (rawPdfLocation != null) {
          const linesFb =
            (pf.metadata?.pdfLocation &&
              typeof pf.metadata.pdfLocation === "object" &&
              !Array.isArray(pf.metadata.pdfLocation) &&
              pf.metadata.pdfLocation.lines) ||
            0;
          pushBindingsFromPdfSlices(bindings, roleKey, sig.signerKey, an, rawPdfLocation, linesFb, ts, nRef,
            Object.keys(extra).length > 0 ? extra : undefined);
          return;
        }
        // No field-level `pdfLocation` — but radios / checkboxes /
        // *WithOther variants put their PDF placements on
        // `metadata.options[].pdfLocation` and/or
        // `metadata.other.{radio,checkbox,input}.pdfLocation`. The
        // binding still has to exist so the builder can re-attach
        // the field to the right (role, signer) row on the next
        // save — without it, these fields silently disappear from
        // signaturesJSON. Binding's pdfLocation is `null`; the real
        // placements stay on the field's metadata in app state.
        const hasOptionPlacements =
          Array.isArray(pf.metadata?.options) &&
          pf.metadata.options.some((opt) => opt?.pdfLocation);
        const o = pf.metadata?.other;
        const hasOtherPlacements =
          o &&
          typeof o === "object" &&
          [o.radio, o.checkbox, o.input].some(
            (slot) => slot && typeof slot === "object" && slot.pdfLocation,
          );
        if (hasOptionPlacements || hasOtherPlacements) {
          bindings.push({
            id: `signer-bind-${ts}-${nRef.n++}`,
            roleKey,
            signerKey: sig.signerKey,
            attributeName: an,
            pdfLocation: null,
            lines: 0,
            ...(pf.label != null && pf.label !== "" ? { label: pf.label } : {}),
            ...(def ? { def } : {}),
          });
        }
      });
    });
  }
  return bindings;
}

/**
 * Remove signer-level embedded placements from each signer line (canonical bindings live in signerAttributeBindings state).
 * Strips `fields`, additionalFields, attributePlacements, and additionalPlacements (legacy).
 * @param {Object} signatures
 * @returns {Object}
 */
export function stripAttributePlacementsFromSignatures(signatures) {
  if (!signatures || typeof signatures !== "object") return {};
  return Object.fromEntries(
    Object.entries(signatures).map(([roleKey, roleValue]) => {
      if (!roleValue || typeof roleValue !== "object") return [roleKey, roleValue];
      const sigs = Array.isArray(roleValue.signatures) ? roleValue.signatures : [];
      return [
        roleKey,
        {
          ...roleValue,
          signatures: sigs.map((sig) => {
            if (!sig || typeof sig !== "object") return sig;
            const { fields, additionalFields, attributePlacements, additionalPlacements, ...rest } = sig;
            return rest;
          }),
        },
      ];
    }),
  );
}

/**
 * Cloud / auto-save restore: prefer explicit signerAttributeBindings; otherwise extract from embedded signer fields (legacy: additionalFields / attributePlacements / additionalPlacements).
 * Returns signatures with those arrays stripped (canonical bindings live in signerAttributeBindings).
 */
export function resolveSignerAttributeBindingsForLoad(signatures, savedBindings) {
  if (!signatures || typeof signatures !== "object") {
    return {
      signatures: {},
      bindings: Array.isArray(savedBindings) ? savedBindings : [],
    };
  }
  const stripped = stripAttributePlacementsFromSignatures(signatures);
  if (Array.isArray(savedBindings) && savedBindings.length > 0) {
    return { signatures: stripped, bindings: savedBindings };
  }
  return {
    signatures: stripped,
    bindings: extractSignerAttributeBindingsFromSignatures(signatures),
  };
}
